import { useEffect, useMemo, useState } from "react";
import apiService, { handleError } from "../../Common/ApiCommon/ApiService";
import icons from "../../../assets/images/constantIcons";
import EditRoleForm from "./EditRoleForm";

const useAddRoles = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [editPopupIndex, setEditPopupIndex] = useState(null);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);

  // Open edit form
  const handleEditPopup = (id) => {
    setEditPopupIndex(id);
  };

  // Fetch Roles
  const fetchRoles = async () => {
    setLoading(true);
    try {
      const response = await apiService.get("roles");
      setRoles(response.roles);
    } catch (errors) {
      handleError(errors)
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRoles();
  }, []);

  // Roles Headings
  const columns = useMemo(
    () => [
      {
        Header: "Id",
        accessor: "id",
      },
      {
        Header: "Role Name",
        accessor: "name",
        headerAlign: "text-left",
        cellAlign: "text-left",
        Cell: ({ value }) => (
          <span>{value}</span>
        ),
      },
      {
        Header: "Module Access",
        accessor: "module_access",
        headerAlign: "text-left",
        cellAlign: "text-left",
        Cell: ({ value }) => (
          <span className="">{Array.isArray(value) ? value.join(", ") : "No Access"}</span>
        ),
      },
      {
        Header: "Action",
        accessor: "action",
        headerAlign: "text-left",
        cellAlign: "text-center",
        disableSortBy: true ,
        Cell: ({ row }) => {
          return (
            <div className="flex justify-start md:justify-center">
              <button
                onClick={() => handleEditPopup(row.original.id)}
                className="p-2 flex items-center justify-center focus:outline-none w-[40px] h-[40px] rounded-full bg-green50 text-green cursor-pointer"
              >
                <icons.FaRegEdit className="text-xl font-bold" />
              </button>
              {
                editPopupIndex === row.original.id && (
                  <EditRoleForm
                    editPopup={editPopupIndex === row.original.id}
                    onClose={() => handleEditPopup(null)}
                    roleId={row.original.id}
                    fetchRoles={fetchRoles}
                  />
                )
              }
            </div>
          )
        },
      },
    ],
    [editPopupIndex]
  );

  // Roles Data
  const data = useMemo(() => {
    if (!Array.isArray(roles)) return [];
    return roles.map((role) => ({
      id: role.id,
      name: role.name,
      module_access: Array.isArray(role.module_access)
        ? role.module_access.map((module) =>
          module.charAt(0).toUpperCase() + module.slice(1)
        )
        : [],
    }));
  }, [roles]);

  // Pagination
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = data.slice(indexOfFirstRow, indexOfLastRow);

  // Handle Pagination
  const handlePageChange = (newPage) => setCurrentPage(newPage);
  const handleRowsPerPageChange = (newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setCurrentPage(1);
  };

  return {
    columns,
    currentRows,
    totalRows: data.length,
    currentPage,
    rowsPerPage,
    handlePageChange,
    handleRowsPerPageChange,
    fetchRoles,
    loading
  };
};

export default useAddRoles;
